import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Accordion from "../Accordion/Accordion";
import { getCombinedFilters } from "../../utils/filter";
import "./FilterSection.scss";
import MinMaxFilter from "./MinMaxFilter";
import FullTextFilter from "./FullTextFilter";
import SelectionFilter from "./SelectionFilter";
import Authorization from "../Authorization/Authorization";
import { useDispatch, useSelector } from "react-redux";
import { filtersUpdated } from "../../redux/reducer";



function FilterSection({ totalDisplayedResults, removeFilterHandler }) {
  const [ra, setRaValue] = useState({min:"",max:""});
  const [dec, setDecValue] = useState({min:"",max:""});
  const [fluxDensity, setFluxDensity] = useState({min:"",max:""});
  const [nGauss, setNGauss] = useState({min:"",max:""});
  const [distanceNN, setDistanceNN] = useState({min:"",max:""});
  const [realSource, setRealSource] = useState({ type: "select",value:"Any"});
  const [SISPWFIT, setSISPWFIT] = useState({ dtype: "array" });
  const [pointingName, setPointingName] = useState({ type: "full-text",value:"" });
  const [areFiltersValid, setAreFiltersValid] = useState(false);

  const FILTERS = [
    {
      label: "RA",
      value: "ra",
      name:"ra",
      placeholder: "HH:MM:SS.ss",
      type: "text",
      unit: "",
      stateValues: ra,
      auth: false,
    },
    {
      label: "DEC",
      value: "dec",
      name:"dec",
      placeholder: "DD:MM:SS.ss",
      type: "text",
      unit: "",
      stateValues: dec ,
      auth: false,
    },
    {
      label: "FLUX Density",
      value: "flux",
      placeholder: "",
      type: "number",
      unit: "mJy",
      stateValues: fluxDensity,
      auth: false,
    },
    {
      label: "Pointing ID",
      value: "pointing_name",
      placeholder: "",
      type: "full-text",
      unit: "",
      stateValues: pointingName,
      auth: false,
    },
  ];
  
  const AUTH_FILTERS = [
    {
      label: "N Gauss",
      value: "n_gauss",
      placeholder: "",
      type: "number",
      unit: "",
      stateValues: nGauss,
      auth: true,
    },
    {
      label: "Distance NN",
      value: "distance_nn",
      placeholder: "",
      type: "number",
      unit: "arcsec",
      stateValues: distanceNN,
      auth: true,
    },
    {
      label: "Real Source",
      value: "real_source",
      placeholder: "",
      type: "select",
      options: [
        { name: "Any", value: "ANY" },
        { name: "Yes", value: true },
        { name: "No", value: false },
      ],
      selected: "Any",
      unit: "",
      stateValues: realSource,
      auth: true,
    },
    {
      label: "Spectral Index SPWFIT",
      value: "spectral_index_spwfit",
      placeholder: "",
      type: "number",
      unit: "",
      auth: true,
      dtype: "array",
      stateValues: SISPWFIT,
      index: 0,
    },
  ];
  // const formRef = useRef(null);

  const dispatch = useDispatch();
  const { selectedSPW } = useSelector((state) => state.router);

  useEffect(() => {
    setFilterValidity();
  }, [
    ra,
    dec,
    fluxDensity,
    nGauss,
    distanceNN,
    realSource,
    SISPWFIT,
    pointingName,
  ]);

  useEffect(() => {
    const filters = getCombinedFilters(generateFilterObject());
    dispatch(filtersUpdated({ filters }));
  }, [selectedSPW]);

  function filterChangeHandler(event, filterIdentifier, minOrMax) {
    const { value } = event.target;
    const filterMap = {
      ra: () => setRaValue({ ...ra, [minOrMax]: value }),
      dec: () => setDecValue({ ...dec, [minOrMax]: value }),
      flux: () => setFluxDensity({ ...fluxDensity, [minOrMax]: value }),
      n_gauss: () => setNGauss({ ...nGauss, [minOrMax]: value }),
      distance_nn: () => setDistanceNN({ ...distanceNN, [minOrMax]: value }),
      real_source: () => setRealSource({ ...realSource, [minOrMax]: value }),
      spectral_index_spwfit: () =>
        setSISPWFIT({ ...SISPWFIT, [minOrMax]: value }),
      pointing_name: () =>
        setPointingName({ ...pointingName, [minOrMax]: value }),
    };

    if (filterMap[filterIdentifier] !== undefined) {
      filterMap[filterIdentifier]();
    }
  }

  function isRaFilterValid() {
    return !!(ra.min && ra.max);
  }
  function isDecFilterValid() {
    return !!(dec.min && dec.max);
  }
  function isFluxDensityFilterValid() {
    return !!(fluxDensity.min && fluxDensity.max);
  }
  function isNGaussFilterValid() {
    return !!(nGauss.min && nGauss.max);
  }
  function isDistNNFilterValid() {
    return !!(distanceNN.min && distanceNN.max);
  }
  function isRealSourceFilterValid() {
    return realSource.value !== "Any";
  }
  function isSISPWFitFilterValid() {
    return !!(SISPWFIT.min && SISPWFIT.max);
  }
  function isPointingNameFilterValid() {
    return !!pointingName.value;
  }

  function setFilterValidity() {
    setAreFiltersValid(
      isRaFilterValid() ||
        isDecFilterValid() ||
        isFluxDensityFilterValid() ||
        isNGaussFilterValid() ||
        isDistNNFilterValid() ||
        isRealSourceFilterValid() ||
        isSISPWFitFilterValid() ||
        isPointingNameFilterValid()
    );
  }

  function generateFilterObject() {
    const filters = {
      ra: {},
      dec: {},
      flux: {},
      n_gauss: {},
      distance_nn: {},
      real_source: {},
      spectral_index_spwfit: {},
      pointing_name: {},
    };
    isRaFilterValid() ? (filters.ra = ra) : delete filters.ra;
    isDecFilterValid() ? (filters.dec = dec) : delete filters.dec;
    isFluxDensityFilterValid()
      ? (filters.flux = fluxDensity)
      : delete filters.flux;
    isNGaussFilterValid() ? (filters.n_gauss = nGauss) : delete filters.n_gauss;
    isDistNNFilterValid()
      ? (filters.distance_nn = distanceNN)
      : delete filters.dist_nn;
    isRealSourceFilterValid()
      ? (filters.real_source = realSource)
      : delete filters.real_source;
    isSISPWFitFilterValid()
      ? (filters.spectral_index_spwfit = SISPWFIT)
      : delete filters.spectral_index_spwfit;
    isPointingNameFilterValid()
      ? (filters.pointing_name = pointingName)
      : delete filters.pointing_name;
    return filters;
  }

  function applyFilters() {
    const filters = getCombinedFilters(generateFilterObject());
    dispatch(filtersUpdated({ filters }));
  }

  function resetFilters() {
    setRaValue({min:"",max:""});
    setDecValue({min:"",max:""});
    setFluxDensity({min:"",max:""});
    setNGauss({min:"",max:""});
    setDistanceNN({min:"",max:""});
    setRealSource({ type: "select",value:"Any"});
    setSISPWFIT({ dtype: "array" });
    setPointingName({ type: "full-text" ,value:""});
    // formRef.current.reset();
    removeFilterHandler();
    dispatch(filtersUpdated({filters:{}}));
  }

  function renderFilterSection(filter) {
    return (
      <div className="filter" key={filter.label}>
        <Accordion expanded={true}>
          <p className="name">
            {filter.label} {filter.unit ? `(${filter.unit})` : ""}
          </p>
          {renderFilter(filter, filterChangeHandler)}
        </Accordion>
      </div>
    );
  }

  function renderFilter(filter, changeHandler) {
    if (filter.type === "select") {
      return (
        <SelectionFilter
          filter={filter}
          onChange={changeHandler}
          val={filter.stateValues}
          defaultValue={realSource.value}
        />
      );
    }
    // filter.name['value']

    if (filter.type === "full-text") {
      return <FullTextFilter filter={filter} onChange={changeHandler}  val={filter.stateValues}/>;
    }

    return <MinMaxFilter filter={filter} onChange={changeHandler}  val={filter.stateValues} />;
  }

  return (
    <div className="filter-container">
      <div className="header">
        <h3>Filters</h3>
      </div>
      <p className="total-results">
        <b>{totalDisplayedResults}</b> results found
      </p>
      <form
      //  ref={formRef}
       >
        {FILTERS.map(renderFilterSection)}
        <Authorization authenticateOnly={true}>
          {AUTH_FILTERS.map(renderFilterSection)}
        </Authorization>
      </form>
      <div className="actions">
        <button className="secondary" onClick={resetFilters}>
          Reset all filters
        </button>
        <button
          disabled={!areFiltersValid}
          className="primary"
          onClick={applyFilters}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

FilterSection.propTypes = {
  totalDisplayedResults: PropTypes.number.isRequired,
  removeFilterHandler: PropTypes.func.isRequired,
};

export default FilterSection;
